import { z } from 'zod';

/**
 * Primitivas compartilhadas por todos os contratos.
 *
 * Regra do projeto (Cap. 2.1 do documento funcional): nenhuma regra comercial
 * pode nascer no codigo-fonte. Todo valor de negocio trafega por estes tipos,
 * gerado pelo painel administrativo e apenas interpretado pelo front-end.
 */

/** Chave estavel usada em configuracao. Nunca deve ser renomeada apos publicada. */
export const configKey = z
  .string()
  .min(1)
  .max(64)
  .regex(/^[a-z][a-z0-9_]*$/, 'Use snake_case iniciando por letra.');

export const slug = z
  .string()
  .min(1)
  .max(120)
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Use kebab-case.');

/** Identificador de qualquer entidade persistida (ULID/UUID). */
export const entityId = z.string().min(8).max(64);

/** Texto que o administrador pode editar sem deploy. Ver `TextTokens`. */
export const editableText = z.string();

/** Valores monetarios trafegam em centavos para nao sofrerem erro de ponto flutuante. */
export const cents = z.number().int().min(0);

export const isoDateTime = z.string().datetime({ offset: true });

/** Tudo que o admin pode ativar/desativar carrega este envelope. */
export const publishable = z.object({
  enabled: z.boolean().default(true),
  /** Menor = aparece antes. O admin reordena sem tocar em codigo. */
  order: z.number().int().default(0),
  /** Janela opcional de vigencia — usada por promocoes, banners e campanhas. */
  startsAt: isoDateTime.nullable().default(null),
  endsAt: isoDateTime.nullable().default(null),
});

export type Publishable = z.infer<typeof publishable>;

/**
 * Expressao de condicao avaliada no cliente e no servidor pelo mesmo
 * interpretador (`@solote/contracts/expression`). Mantemos deliberadamente
 * pequena: sem funcoes, sem acesso a globals, sem eval.
 */
export const conditionOperator = z.enum([
  'eq',
  'neq',
  'gt',
  'gte',
  'lt',
  'lte',
  'in',
  'not_in',
  'contains',
  'is_empty',
  'is_not_empty',
]);

export const condition = z.object({
  /** Caminho no objeto de contexto, ex.: "topografia" ou "plano.slug". */
  path: z.string().min(1),
  op: conditionOperator,
  value: z.unknown().optional(),
});

export type Condition = z.infer<typeof condition>;

export const conditionGroup = z.object({
  mode: z.enum(['all', 'any']).default('all'),
  rules: z.array(condition).default([]),
});

export type ConditionGroup = z.infer<typeof conditionGroup>;

/** Restringe algo a determinados perfis e/ou planos. */
export const accessGate = z.object({
  roles: z.array(z.string()).default([]),
  plans: z.array(slug).default([]),
});

export type AccessGate = z.infer<typeof accessGate>;

export const auditStamp = z.object({
  createdAt: isoDateTime,
  updatedAt: isoDateTime,
  updatedBy: entityId.nullable().default(null),
});
